import React from "react";
import { useForm } from "react-hook-form";
import { Controller } from "react-hook-form";
import Input from "../../components/global/Input";
import Button from "../../components/global/Button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useCompleteRegisterWithGoogleMutation,
  useRegisterMutation,
} from "../../lib/redux/services/sections/Auth";
import PhoneNumberInput from "../global/PhoneNumberInput";
import { EmailValidate, toastMessage } from "../../utils/utils";
import ValidateError from "../global/ValidateError";
import TermsAndConditions from "./TermsAndConditions";
import CheckBox from "../global/CheckBox";
import { useGetDictionary } from "../../hooks/useGetDictionary";
import { DictionaryType } from "../../types";
import EyeClosedIcon from "../../assets/icons/EyeClosedIcon";
import EyeOpenedIcon from "../../assets/icons/EyeOpenedIcon";

type FormInput = {
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  password: string;
  confirm_password: string;
  mobile_country: string;
  org_name: string;
  org_address: string;
  org_phone: string;
  org_country: string;
  position: string;
  agree_terms: boolean;
};

const RegisterForm = () => {
  const location = useLocation();

  const { Auth, inputs, shared }: DictionaryType = useGetDictionary();
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid, dirtyFields },
  } = useForm<FormInput>({
    mode: "onChange",
    defaultValues: location.state
      ? { email: location.state.user.email }
      : undefined,
  });

  const navigate = useNavigate();
  const [register] = useRegisterMutation();
  const [registerWithGoogle] = useCompleteRegisterWithGoogleMutation();

  const registerApi = location.state ? registerWithGoogle : register;

  const onSubmit = async (formData: FormInput) => {
    if (formData.password !== formData.confirm_password) {
      toast.error(Auth?.password_confirm_message);
      return;
    }

    try {
      const data = await toast.promise(
        registerApi({...formData,email:formData?.email?.toLowerCase()}).unwrap(),
        toastMessage()
      );

      if (!location.state) {
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("mocion-admin-id", data?.user?.id);
        navigate("/auth/email-confirmation");
      } else {
        navigate("/auth/first-club");
      }
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4 md:space-y-4 max-w-[814px]  "
    >
      <div className="  px-12 space-y-4 ">
        {/* Row 1: First Name and Last Name */}
        <div className=" flex text-start flex-row items-basline justify-center gap-6 w-full">
          <div className="w-full">
            <Controller
              name="first_name"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <Input
                  inputProps={{
                    ...field,
                    type: "text",
                  }}
                  label={inputs.first_Name}
                />
              )}
            />
            {errors.first_name && <ValidateError />}
          </div>
          <div className="w-full">
            <Controller
              name="last_name"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <Input
                  inputProps={{
                    ...field,
                    type: "text",
                  }}
                  label={inputs.last_name}
                />
              )}
            />
            {errors.last_name && <ValidateError />}
          </div>
        </div>

        {/* Row 2: Email and Mobile */}
        <div className="flex text-start flex-row items-basline justify-center gap-6 w-full">
          <div className="w-full">
            <Controller
              name="email"
              rules={{
                required: true,
                pattern: {
                  value: EmailValidate,
                  message: shared.Invalid_email_domain_messsage,
                },
              }}
              control={control}
              render={({ field }) => (
                <Input
                  inputProps={{
                    readOnly: location.state ? true : false,
                    ...field,
                    type: "email",
                  }}
                  label={inputs.email}
                />
              )}
            />
            {errors.email && (
              <ValidateError errorMessage={errors.email.message} />
            )}
          </div>
          <div className="w-full">
            <Controller
              name="mobile"
              rules={{
                required: shared.phone_number_validate_message,
                minLength: {
                  value: 11,
                  message: shared.phone_number_validate_message,
                },
              }}
              control={control}
              render={({ field }) => (
                <PhoneNumberInput
                  field={field}
                  label={inputs.mobile_number}
                  name="mobile"
                  value={field.value}
                  handleChange={(value, countryCode) => {
                    field.onChange(value); // Update phone number
                    setValue("mobile_country", countryCode);
                  }}
                />
              )}
            />

            {errors.mobile && (
              <ValidateError errorMessage={errors?.mobile?.message} />
            )}
          </div>
        </div>

        {/* Row 4: position */}
        <div className="flex text-start flex-row items-center justify-center  gap-6  ">
          <div className="w-full">
            <Controller
              name="org_name"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <Input
                  inputProps={{
                    ...field,
                    type: "text",
                  }}
                  label={inputs.organiztion_name}
                />
              )}
            />
            {errors.org_name && <ValidateError />}
          </div>
          <div className="w-full">
            <Controller
              name="position"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <Input
                  inputProps={{
                    ...field,
                    type: "text",
                  }}
                  label={inputs.role}
                />
              )}
            />
            {errors.position && <ValidateError />}
          </div>
        </div>

        {/* Row 3: Password and Confirm Password */}
        {!location.state && (
          <div className="flex text-start flex-row items-baseline justify-center gap-6 w-full">
            <div className="w-full">
              <Controller
                name="password"
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <Input
                    inputProps={{
                      ...field,
                      type: "password",
                    }}
                    label={inputs.password}
                    icon={EyeClosedIcon}
                    toggleIcon={EyeOpenedIcon}
                  />
                )}
              />
              {errors.password && <ValidateError />}
            </div>
            <div className="w-full">
              <Controller
                name="confirm_password"
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <Input
                    inputProps={{
                      ...field,
                      type: "password",
                    }}
                    label={inputs.confirm_password}
                    icon={EyeClosedIcon}
                    toggleIcon={EyeOpenedIcon}
                  />
                )}
              />
              {errors.confirm_password && <ValidateError />}
            </div>
          </div>
        )}

        <div className="flex flex-row items-center justify-between gap-1 w-full pt-6 ">
          <div className="flex flex-col text-sm ">
            <Controller
              name="agree_terms"
              rules={{ required: Auth?.regiter_page.agree_terms_validation }}
              control={control}
              render={({ field }) => (
                <CheckBox
                  //@ts-ignore
                  checkboxProps={{ ...field, name: "agree_terms" }}
                  isCustom={true}
                  label={Auth?.regiter_page.agree_check}
                  customClassName="w-[15px] h-[15px] m-0 cursor-pointer"
                  customeCheckBoxClass="w-[15px] h-[15px] m-0"
                />
              )}
            />
            {errors.agree_terms && (
              <ValidateError errorMessage={errors.agree_terms.message} />
            )}
          </div>
          <TermsAndConditions customStyle="text-sm" />
        </div>
      </div>

      <div
        // style={{
        //   boxShadow: "0px 0px 0px #888, 0px -5px 4px rgba(71,71,71,0.1) ",
        // }}
        className=" flex flex-row justify-center items-center pt-4  h-fit w-full "
      >
        <Button
          customeStyle={`${
            !isValid ? "bg-gray-500 text-white" : "bg-primary text-white"
          }bg-primary text-white w-full rounded-full text-sm py-3 font-bold`}
          //href="/auth/login"
          text={Auth?.register}
          width="max-w-[350px] w-full"
          type="submit"
          //buttonProps={{ disabled: !isDirty || !isValid }}
        />
      </div>
    </form>
  );
};

export default RegisterForm;
